/**
 * Human-readable formatting for provenance timestamps.
 *
 * Data pipelines hand us raw ISO strings (e.g. "2026-05-18T21:06:43.362577").
 * Those were being rendered verbatim in provenance rows. Formatting happens
 * here so every surface that shows a retrieval time reads the same way.
 *
 * Locale and time zone are pinned to keep server and client output identical —
 * these strings render during SSR, and a locale- or zone-dependent format would
 * produce hydration mismatches.
 */

const DATE_FORMAT = new Intl.DateTimeFormat('en-US', {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  timeZone: 'UTC',
});

const DATE_TIME_FORMAT = new Intl.DateTimeFormat('en-US', {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  timeZone: 'UTC',
});

/**
 * Formats an ISO-ish timestamp for display. Values that are not parseable dates
 * are returned unchanged, so callers can keep passing prose fallbacks like
 * "Timestamp unavailable".
 *
 * A timestamp at exact UTC midnight is treated as date-only precision and
 * rendered without a time, which is how the checked-in dataset snapshots land.
 */
export function formatTimestamp(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return value;

  // Reject prose ("on an unavailable date") and bare numbers that Date would
  // otherwise coerce into a nonsensical result.
  if (!/^\d{4}-\d{2}-\d{2}/.test(trimmed)) return value;

  // Pipelines emit naive timestamps ("2026-05-18T21:06:43.362577") with no zone.
  // `new Date` would read those as local time and then render them shifted into
  // UTC, which can move the displayed date to the previous or next day. Pinning
  // them to UTC keeps the wall-clock reading the pipeline actually recorded.
  const hasZone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(trimmed);
  const parsed = new Date(hasZone || !trimmed.includes('T') ? trimmed : `${trimmed}Z`);
  if (Number.isNaN(parsed.getTime())) return value;

  const isDateOnly =
    !trimmed.includes('T') ||
    (parsed.getUTCHours() === 0 && parsed.getUTCMinutes() === 0 && parsed.getUTCSeconds() === 0);

  return isDateOnly ? DATE_FORMAT.format(parsed) : DATE_TIME_FORMAT.format(parsed);
}
